import {
  Col,
  DatePicker,
  Form,
  Layout,
  message,
  Modal,
  Row,
  Space,
  Avatar,
  Badge,
  Dropdown,
  Menu,
  Tooltip,
} from "antd"
import { useNavigate } from "react-router-dom"
import MainMenu from "./MainMenu"
import { useEffect, useRef, useState } from "react"
import apiClient from "../Utils/apiClient"
import { useHeaderContext } from "../Utils/HeaderContext"
import { useDispatch, useSelector } from "react-redux"
import { LogoutCurve, Personalcard, Setting, Sms } from "iconsax-react"
import { GoBell } from "react-icons/go"
import JobsDrawer from "../components/JobsDrawer/JobsDrawer"
import "./Header.scss"
import { updateUserStatus } from "../Redux/Slices/UserSlice"
import { assetUrl, companyName, productName } from "../config"
import { currentTenant } from "../Utils/tenantStorage"
import { getLiveSocket } from "../Utils/liveSocket"
import dayjs from "dayjs"
const { Header: AntHeader } = Layout

const Header = () => {
  const [form] = Form.useForm()
  const [dateRange, setDateRange] = useState([])
  const socket = getLiveSocket()
  const statusRef = useRef(null)
  const userInfo = useSelector((state) => state.user.data)
  const [device, setDevice] = useState({})
  const [isUpdateStatus, setIsUpdateStatus] = useState(false)
  const [showModalForLeaveApply, setShowModalForLeaveApply] = useState(false)
  const [imageUrl, setImageUrl] = useState("")
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const [showStatusMenu, setShowStatusMenu] = useState(false)

  const linkedDevices = Array.isArray(userInfo?.deviceData)
    ? userInfo.deviceData.filter((row) => row && row._id)
    : userInfo?.deviceData?._id
      ? [userInfo.deviceData]
      : []
  const primaryDeviceId =
    linkedDevices[0]?._id ||
    userInfo?.deviceIds?.[0]?._id ||
    userInfo?.deviceIds?.[0] ||
    userInfo?.deviceId
  const primaryDeviceHostname =
    linkedDevices[0]?.hostname ||
    (Array.isArray(userInfo?.deviceData)
      ? userInfo?.deviceData?.[0]?.hostname
      : userInfo?.deviceData?.hostname)

  const { render } = useHeaderContext()
  const { RangePicker } = DatePicker
  const handleClose = () => setShowModalForLeaveApply(false)

  const handleLogout = () => {
    localStorage.removeItem("user")
    navigate("/login")
    apiClient("/auth/logout")
  }

  const handleBellClick = async () => {
    if (userInfo?.role === "network-admin") {
      navigate("/all-logs")
    }
  }
  const handleOpenUserLogs = async () => {
    if (userInfo?.role === "user") {
      navigate(`/logs/${primaryDeviceHostname}`)
    }
  }

  const updateStatus = async (status) => {
    if (!primaryDeviceId) {
      message.error("No device is linked to this account")
      return
    }
    try {
      const res = await apiClient(
        `devices/update-userStatus/${primaryDeviceId}`,
        "POST",
        { status }
      )

      if (!res?.success) {
        message.error(res?.message || "Could not update status")
        return
      }

      if (res.data) setDevice(res.data)
      else setIsUpdateStatus((pre) => !pre)
      dispatch(updateUserStatus(status))
      message.success("Status updated")
      if (socket) {
        socket.emit("set-status", {
          userId: userInfo?._id,
          status,
        })
      }
      setShowStatusMenu(false)
    } catch (err) {
      message.error(err.message || "Could not update status")
    }
  }
  const getUser = async () => {
    try {
      const res = await apiClient(`auth/getby-user/${userInfo?._id}`)
      setImageUrl(assetUrl(res?.result?.profileImage))
    } catch (err) {
      message.error("Error while getting user in header", err)
    }
  }
  const getDevice = async (id) => {
    try {
      if (id) {
        const device = await apiClient(`devices/${id}`, "GET")
        if (device) {
          setDevice(device)
        }
      }
    } catch (err) {
      console.log("err while fetching deviceData from Header", err)
    }
  }

  useEffect(() => {
    getDevice(primaryDeviceId)
    if (!socket) return undefined
    const onStatus = ({ userId, status }) => {
      if (userId === userInfo?._id) {
        dispatch(updateUserStatus(status))
      }
    }
    socket.on("status-updated", onStatus)
    return () => socket.off("status-updated", onStatus)
  }, [isUpdateStatus, dispatch, primaryDeviceId, socket, userInfo?._id])
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target)) {
        setShowStatusMenu(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const fetchUnreadLogs = async () => {
      try {
        const res = await apiClient("logs/unread-count", "post")
        setUnreadCount(res.count || 0)
      } catch (error) {
        console.error("Failed to fetch unread logs:", error)
      }
    }

    fetchUnreadLogs()
    getUser()
  }, [render, userInfo])

  const appName = productName
  const tenantSlug = currentTenant() || userInfo?.tenant?.slug
  const trialEnds = userInfo?.tenant?.status === "trial" ? userInfo?.tenant?.trialEndsAt : null
  const trialLabel = trialEnds
    ? ` · trial until ${new Date(trialEnds).toLocaleDateString()}`
    : ""

  const getUserLeaveDates = async () => {
    const res = await apiClient(`devices/${primaryDeviceId}`, "GET")
    const leave = res?.userStatus?.leave

    if (leave?.fromDate && leave?.toDate) {
      const from = dayjs(leave.fromDate)
      const to = dayjs(leave.toDate)

      setDateRange([from, to])

      form.setFieldsValue({ range: [from, to] })
    } else {
      setDateRange([])
      form.setFieldsValue({ range: [] })
    }
  }

  useEffect(() => {
    if (showModalForLeaveApply) {
      getUserLeaveDates()
    }
  }, [showModalForLeaveApply])

  const handleApplyLeave = async () => {
    const values = await form.validateFields()
    const [start, end] = values.range

    const startISO = dayjs(start).startOf("day").toISOString()
    const endISO = dayjs(end).endOf("day").toISOString()

    const payload = {
      leave: { fromDate: startISO, toDate: endISO },
    }

    const updateUser = await apiClient(
      `/devices/usersLeave/${primaryDeviceId}`,
      "POST",
      payload
    )

    if (updateUser?.success) {
      updateStatus("OnLeave")
      setDateRange([])
      handleClose()
    }
  }

  const profileContent = () => {
    return (
      <Menu>
        <Menu.Item key="user">
          <Personalcard size={20} className="me-1" />{" "}
          {userInfo ? userInfo.name : ""}
        </Menu.Item>
        <Menu.Item key="email">
          <Sms size={20} className="me-1" /> {userInfo ? userInfo.email : ""}
        </Menu.Item>
        {userInfo?.isPlatformAdmin ? (
          <Menu.Item
            className="text-primary"
            key="organizations"
            onClick={() => navigate("/platform/organizations")}
          >
            Organizations
          </Menu.Item>
        ) : null}
        <Menu.Item
          className="text-primary"
          key="settings"
          onClick={() => navigate("/settings")}
        >
          <Setting size={20} className="me-1" /> Settings
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item className="text-primary" key="logout" onClick={handleLogout}>
          <LogoutCurve size={22} className="me-1" /> Logout
        </Menu.Item>
      </Menu>
    )
  }
  return (
    <>
      <AntHeader className="app-header">
        <div className="app-header__menu-toggle">
          <MainMenu />
        </div>
        <div className="app-header__meta">
          <span className="app-header__eyebrow">
            {companyName}
            {tenantSlug ? ` · ${tenantSlug}` : ""}
            {trialLabel}
          </span>
          <span className="app-header__title">{appName}</span>
        </div>
        <Space className="app-header__actions" size={12} align="center">
          <div className="app-header__user">
            {userInfo?.role === "network-admin" ? (
              <>
                <JobsDrawer />
                <Tooltip title="Notifications">
                  <button
                    type="button"
                    className="app-header__icon-btn"
                    onClick={handleBellClick}
                    aria-label="Notifications"
                  >
                    <Badge count={unreadCount} size="small" offset={[-1, 2]}>
                      <GoBell size={18} />
                    </Badge>
                  </button>
                </Tooltip>
              </>
            ) : (
              <JobsDrawer />
            )}

            {userInfo?.role !== "network-admin" && primaryDeviceId ? (
                <div className="position-relative" ref={statusRef}>
                  <Tooltip title={device?.userStatus?.status || "Set your status"}>
                    <div
                      className="d-flex align-items-center status-display"
                      onClick={() => setShowStatusMenu((pre) => !pre)}
                    >
                      <span
                        className={`status-dot ${
                          device?.userStatus?.status || "Offline"
                        }`}
                      ></span>
                      <span className="status-text ms-2 text-capitalize">
                        {device?.userStatus?.status || "Set status"}
                      </span>
                    </div>
                  </Tooltip>

                  {showStatusMenu && (
                    <div className="status-dropdown">
                      <div
                        className="status-option"
                        onClick={() => updateStatus("Online")}
                      >
                        <span className="dot Online"></span> Online
                      </div>
                      <div
                        className="status-option"
                        onClick={() => updateStatus("OnBreak")}
                      >
                        <span className="dot OnBreak"></span> On Break
                      </div>
                      <div
                        className="status-option"
                        onClick={() => updateStatus("Offline")}
                      >
                        <span className="dot Offline"></span> Offline
                      </div>
                      <div
                        className="status-option"
                        onClick={() => setShowModalForLeaveApply(true)}
                      >
                        <span className="dot OnLeave"></span> On Leave
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

            <Dropdown
              overlay={profileContent()}
              trigger={["click"]}
              arrow
            >
              <button type="button" className="app-header__profile">
                <div className="app-header__identity">
                  <span className="app-header__name">
                    {userInfo?.name || "User"}
                  </span>
                  <span className="app-header__role">
                    {userInfo?.role || "member"}
                  </span>
                </div>
                <Avatar
                  size={36}
                  src={imageUrl}
                  className="cursor-pointer bg-primary text-uppercase fw-semibold"
                >
                  {!userInfo?.profileImage &&
                    userInfo?.name?.[0]?.toUpperCase()}
                </Avatar>
              </button>
            </Dropdown>
          </div>
        </Space>
      </AntHeader>
      {showModalForLeaveApply && (
        <Modal
          open={showModalForLeaveApply}
          onClose={handleClose}
          onCancel={handleClose}
          onOk={handleApplyLeave}
        >
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="range"
                  label="Date"
                  rules={[{ required: true, message: "Please enter a Title" }]}
                >
                  <RangePicker
                    className="w-100"
                    format="YYYY-MM-DD"
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates || [])}
                    disabledDate={(current) =>
                      current && current < dayjs().startOf("day")
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      )}
    </>
  )
}

export default Header
